import { computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { decamelize } from '@ember/string';

import AccountModel, { PlanFeatures } from 'cio/models/account';
import { PIPELINES_ONLY_PLAN_TYPES } from 'cio/models/plan';
import CustomerCounterService from 'cio/services/customer-counter';
import { getExpiresAt, getLimitValue, isExpired } from 'cio/utils/planFeatures';
import { ProductApp } from 'cio/utils/product-app-info';
import BillingService from './billing';

interface PlanFeatureInfo {
  expired: boolean;
  enabled: boolean;
  showPremiumLabel: boolean;
  limit: number | false;
  expiresAt: number | null;
}

export default class AccountService extends Service {
  @service customerCounter!: CustomerCounterService;
  @service billing!: BillingService;

  // external API
  current!: AccountModel;
  @reads('current.planFlags.multismtp') multismtp!: boolean;

  @reads('current.verificationApproved') verificationApproved!: boolean;
  @reads('current.verificationPending') verificationPending!: boolean;

  /** TODO: Should evaluate if plan info is really needed for this check.
   * Plan info is not always available or up to date, and we can use data
   * from the gatedFeatures object to determine if Journeys is available. */
  get isPipelinesOnlyPlan() {
    let planType = this.billing.subscription?.plan?.planType ?? null;

    if (planType == null) {
      return false;
    }

    // If an account is on a CDP Starter plan, but trialing Journeys at the same
    // time, the plan will be a CDP one, and gated features will contain a
    // "messaging" entry with a 14 day limit.
    let isPipelinesOnlyPlanType = PIPELINES_ONLY_PLAN_TYPES.includes(planType);

    let hasJourneysTrial =
      this.current?.gatedFeatures?.[PlanFeatures.Messaging] != null &&
      this.current?.gatedFeatures?.[PlanFeatures.Messaging]?.days > 0;

    return isPipelinesOnlyPlanType === true && !hasJourneysTrial;
  }

  get accessibleProducts(): ProductApp[] {
    if (this.isPipelinesOnlyPlan) {
      return [ProductApp.DATA_PIPELINES];
    }

    return (this.current?.productActivations ?? []) as ProductApp[];
  }

  /* TODO: Should evaluate if we can use `isJourneysAvailable` instead of this
   * getter (or using its implementation) , as it doesn't depend on planType,
   * which is not always available or updated. */
  get isJourneysActivated(): boolean {
    return this.accessibleProducts?.includes(ProductApp.JOURNEYS);
  }

  /** Differs from `isJourneysActivated` in that it doesn't depend on planType,
   * which is not always available or updated. Not changing the other getters
   * for now however because they are used elsewhere.
   * TODO: Should determine if depending on plan information is actually necessary.
   * If not, then most of the getters above can be removed in favor of this one.
   **/
  get isJourneysAvailable(): boolean {
    let { gatedFeatures, productActivations } = this.current ?? {};

    /** This fallback is necessary for some tests that don't set the full account
     * object; in production, this should never happen. */
    if (gatedFeatures == null || productActivations == null) {
      return true;
    }

    return productActivations.includes(ProductApp.JOURNEYS) && gatedFeatures[PlanFeatures.Messaging] != null;
  }

  @computed('current.{gatedFeatures,premiumTrial,premium}')
  get planFeatures(): Record<PlanFeatures, PlanFeatureInfo> {
    // Enables premium label for premium trial users
    let premiumLabelEnabled = this.current?.premiumTrial === true && this.current?.premium === false;

    let features: Record<string, PlanFeatureInfo> = {};
    Object.values(PlanFeatures).forEach((key) => {
      let gatedFeatures = this.current?.gatedFeatures ?? {};
      let feature = gatedFeatures[decamelize(key)];
      let limit = getLimitValue(feature);
      let expired = isExpired(feature);

      /** TODO: This should be a temporary fix for a Backend API problem.
       * The current way to infer if a Journeys trial on a CDP plan has expired
       * is to check if both `journeys` *is present* on `productActivations`, and
       * `messaging` *is not present* on `gatedFeatures`.
       * This is not a "bug" that can be easily fixed, as changing the API might
       * result in unexpected behavior, but we should investigate if we can make
       * it more consistent and simple. */
      if (key === PlanFeatures.Messaging) {
        expired = this.current?.productActivations?.includes?.(ProductApp.JOURNEYS) && feature == null;
      }

      let expiresAt = getExpiresAt(feature);

      let enabled = !expired && feature !== undefined; // if a feature is present on the gatedFeatures object, it is enabled.
      features[key] = {
        expired,
        enabled,
        showPremiumLabel: !enabled || premiumLabelEnabled,
        limit,
        expiresAt,
      };
    });
    return features;
  }

  planLimitReached(planFeature: PlanFeatures): boolean {
    if (planFeature === PlanFeatures.Workspaces) {
      let totalWorkspaces = this.current?.environments.toArray().length;
      let workspacesLimit = this.planFeatures.workspaces.limit;
      return typeof workspacesLimit === 'number' ? workspacesLimit - totalWorkspaces <= 0 : false;
    }
    return false;
  }

  get maxObjectJourneysLimit() {
    return this.current?.gatedFeatures?.[PlanFeatures.MaxObjectJourneys]?.limit;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    account: AccountService;
  }
}
